import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ISiOption } from '../models/special-instructions-form.model';

/** См. комментарий к такому же лимиту в SiEntityAutocompleteComponent. */
const VISIBLE_OPTIONS_LIMIT = 100;

/**
 * Список игнорируемых модификаторов: чипсы выбранного плюс поиск с автодополнением.
 *
 * Идентификатор, которого нет в меню, остаётся в значении и показывается чипсом с
 * предупреждением — выбрасывать его нельзя, иначе открытие экрана меняло бы инструкции.
 */
@Component({
  selector: 'app-si-modifier-chips',
  templateUrl: './si-modifier-chips.component.html',
  styleUrls: ['./si-modifier-chips.component.scss'],
})
export class SiModifierChipsComponent implements OnChanges {
  @Input() value: string[] = [];
  @Input() options: ISiOption[] = [];

  @Output() valueChange = new EventEmitter<string[]>();

  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  public search = '';
  public chips: ISiOption[] = [];
  public visibleOptions: ISiOption[] = [];
  public hiddenOptionsCount = 0;

  private optionById = new Map<string, ISiOption>();

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.options) {
      this.optionById = new Map((this.options ?? []).map(option => [option.id, option]));
    }

    this.rebuildChips();
    this.rebuildVisibleOptions();
  }

  public onSearchChange(): void {
    this.rebuildVisibleOptions();
  }

  public onOptionSelected(id: string): void {
    if (!this.value.includes(id)) {
      this.emit([...this.value, id]);
    }

    this.clearSearch();
  }

  /** Вставленный руками GUID: добавляем по Enter, иначе его некуда деть. */
  public onTokenEnd(rawValue: string): void {
    const id = (rawValue ?? '').trim();

    if (id && !this.value.includes(id)) {
      this.emit([...this.value, id]);
    }

    this.clearSearch();
  }

  public remove(id: string): void {
    this.emit(this.value.filter(item => item !== id));
  }

  public isUnknown(option: ISiOption): boolean {
    return !this.optionById.has(option.id);
  }

  public trackById(_: number, option: ISiOption): string {
    return option.id;
  }

  private clearSearch(): void {
    this.search = '';

    if (this.searchInput) {
      this.searchInput.nativeElement.value = '';
    }

    this.rebuildVisibleOptions();
  }

  private emit(ids: string[]): void {
    this.value = ids;
    this.rebuildChips();
    this.rebuildVisibleOptions();
    this.valueChange.emit(ids);
  }

  private rebuildChips(): void {
    this.chips = (this.value ?? []).map(id => this.optionById.get(id) ?? {id, label: id, hint: ''});
  }

  private rebuildVisibleOptions(): void {
    const query = this.search.trim().toLowerCase();
    const selected = new Set(this.value ?? []);
    const matched = (this.options ?? []).filter(option => !selected.has(option.id) && matches(option, query));

    this.hiddenOptionsCount = Math.max(0, matched.length - VISIBLE_OPTIONS_LIMIT);
    this.visibleOptions = matched.slice(0, VISIBLE_OPTIONS_LIMIT);
  }
}

function matches(option: ISiOption, query: string): boolean {
  if (!query) {
    return true;
  }

  return option.label.toLowerCase().includes(query) || option.hint.toLowerCase().includes(query);
}
